import { useEffect } from 'react';

type PerformanceWithMemory = Performance & {
    memory?: {
        usedJSHeapSize: number;
        jsHeapSizeLimit: number;
    };
};

interface MemoryWatchdogOptions {
    threshold?: number;
    intervalMs?: number;
    cooldownMs?: number;
}

const defaultOptions: Required<MemoryWatchdogOptions> = {
    threshold: 0.95,
    intervalMs: 2000,
    cooldownMs: 120000,
};

export default function useMemoryWatchdog(options?: MemoryWatchdogOptions) {
    const threshold = options?.threshold ?? defaultOptions.threshold;
    const intervalMs = options?.intervalMs ?? defaultOptions.intervalMs;
    const cooldownMs = options?.cooldownMs ?? defaultOptions.cooldownMs;

    useEffect(() => {
        if (import.meta.env.MODE !== 'development') {
            return;
        }

        if (typeof window === 'undefined') {
            return;
        }

        const perf = window.performance as PerformanceWithMemory | undefined;

        if (!perf || !perf.memory) {
            return;
        }

        const storageKey = 'memory_watchdog_disabled';
        if (window.localStorage.getItem(storageKey) === 'true') {
            return;
        }

        let lastAlertAt = 0;
        let dialogOpen = false;
        let disabled = false;

        const showMemoryDialog = (message: string) =>
            new Promise<{ disable: boolean }>((resolve) => {
                const overlay = document.createElement('div');
                overlay.style.position = 'fixed';
                overlay.style.left = '0';
                overlay.style.top = '0';
                overlay.style.width = '100%';
                overlay.style.height = '100%';
                overlay.style.background = 'rgba(0, 0, 0, 0.4)';
                overlay.style.display = 'flex';
                overlay.style.alignItems = 'center';
                overlay.style.justifyContent = 'center';
                overlay.style.zIndex = '99999';

                const dialog = document.createElement('div');
                dialog.style.background = '#ffffff';
                dialog.style.color = '#222222';
                dialog.style.padding = '16px';
                dialog.style.borderRadius = '8px';
                dialog.style.maxWidth = '480px';
                dialog.style.width = '90%';
                dialog.style.boxShadow = '0 10px 30px rgba(0,0,0,0.25)';

                const text = document.createElement('div');
                text.textContent = message;
                text.style.whiteSpace = 'pre-line';
                text.style.marginBottom = '12px';

                const checkboxRow = document.createElement('label');
                checkboxRow.style.display = 'flex';
                checkboxRow.style.alignItems = 'center';
                checkboxRow.style.gap = '8px';
                checkboxRow.style.marginBottom = '12px';

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';

                const checkboxText = document.createElement('span');
                checkboxText.textContent = 'Do not show anymore';

                checkboxRow.appendChild(checkbox);
                checkboxRow.appendChild(checkboxText);

                const actions = document.createElement('div');
                actions.style.display = 'flex';
                actions.style.justifyContent = 'flex-end';

                const okButton = document.createElement('button');
                okButton.type = 'button';
                okButton.textContent = 'OK';
                okButton.style.padding = '6px 12px';
                okButton.style.borderRadius = '6px';
                okButton.style.border = '1px solid #cccccc';
                okButton.style.cursor = 'pointer';

                okButton.addEventListener('click', () => {
                    const disable = checkbox.checked;
                    overlay.remove();
                    resolve({ disable });
                });

                actions.appendChild(okButton);

                dialog.appendChild(text);
                dialog.appendChild(checkboxRow);
                dialog.appendChild(actions);
                overlay.appendChild(dialog);

                document.body.appendChild(overlay);
            });

        const watchdog = window.setInterval(() => {
            if (disabled || dialogOpen) {
                return;
            }

            const memory = perf.memory;
            if (!memory || !memory.jsHeapSizeLimit) {
                return;
            }

            if (Date.now() - lastAlertAt < cooldownMs) {
                return;
            }

            const usage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
            if (usage > threshold) {
                lastAlertAt = Date.now();
                const usagePercent = (usage * 100).toFixed(1);
                console.warn('High memory usage:', `${usagePercent}%`);
                dialogOpen = true;
                const usedGb = (memory.usedJSHeapSize / 1000000000).toFixed(2);
                const limitGb = (memory.jsHeapSizeLimit / 1000000000).toFixed(2);
                showMemoryDialog(
                    `Memory usage is very high (${usagePercent}%). Please save your work or reload.\nusedJSHeapSize: ${usedGb} Gb\njsHeapSizeLimit: ${limitGb} Gb`
                )
                    .then(({ disable }) => {
                        dialogOpen = false;
                        if (disable) {
                            window.localStorage.setItem(storageKey, 'true');
                            disabled = true;
                            window.clearInterval(watchdog);
                        }
                    })
                    .catch(() => {
                        dialogOpen = false;
                    });
            }
        }, intervalMs);

        return () => {
            window.clearInterval(watchdog);
        };
    }, [threshold, intervalMs, cooldownMs]);
}
